import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowUpRight,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CreditCard,
  LoaderCircle,
  RefreshCw,
  Search,
  SlidersHorizontal,
  TrendingUp,
  WalletCards,
  X,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { usePaymentHistory } from '../hooks/usePaymentHistory';
import type { PaymentHistoryItem } from '../services/paymentHistoryService';
import { formatDateString, formatDateTimeString, formatStatus } from '../lib/utils';

type StatusFilter = 'all' | 'completed' | 'failed' | 'pending';

interface MetricCardProps {
  label: string;
  value: string;
  helper: string;
  icon: LucideIcon;
  iconClassName: string;
  iconBackgroundClassName: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  helper,
  icon: Icon,
  iconClassName,
  iconBackgroundClassName,
}) => (
  <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
        <p className="mt-3 truncate text-2xl font-semibold tracking-tight text-slate-950">{value}</p>
        <p className="mt-1 text-xs text-slate-500">{helper}</p>
      </div>
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconBackgroundClassName}`}>
        <Icon className={`h-5 w-5 ${iconClassName}`} aria-hidden="true" />
      </span>
    </div>
  </article>
);

interface DetailItemProps {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}

const DetailItem: React.FC<DetailItemProps> = ({ label, children, wide = false }) => (
  <div className={wide ? 'sm:col-span-2' : undefined}>
    <dt className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</dt>
    <dd className="mt-1 break-words text-sm font-medium text-slate-900">{children}</dd>
  </div>
);

const inputClassName = 'block h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 focus-visible:border-blue-500 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-100';

export const Payments: React.FC = () => {
  const [selectedPayment, setSelectedPayment] = useState<PaymentHistoryItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [serviceProviderTypeFilter, setServiceProviderTypeFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState<string | null>(null);
  const [customEndDate, setCustomEndDate] = useState<string | null>(null);

  const {
    paymentHistory,
    summary,
    pagination,
    loading,
    error,
    setFilters,
    clearFilters,
    refreshPaymentHistory,
  } = usePaymentHistory({ page: 1, limit: 10 });

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const filters: {
        page: number;
        limit: number;
        paymentStatus?: Exclude<StatusFilter, 'all'>;
        search?: string;
        serviceProviderType?: string;
        startDate?: string;
        endDate?: string;
      } = { page: 1, limit: 10 };

      if (statusFilter !== 'all') filters.paymentStatus = statusFilter;
      if (searchTerm.trim()) filters.search = searchTerm.trim();
      if (serviceProviderTypeFilter.trim()) filters.serviceProviderType = serviceProviderTypeFilter.trim();

      if (dateFilter === 'custom') {
        if (customStartDate) filters.startDate = customStartDate;
        if (customEndDate) filters.endDate = customEndDate;
      }

      setFilters(filters);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [statusFilter, searchTerm, serviceProviderTypeFilter, dateFilter, customStartDate, customEndDate, setFilters]);

  useEffect(() => {
    if (!isModalOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsModalOpen(false);
        setSelectedPayment(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'border-emerald-200 bg-emerald-50 text-emerald-700';
      case 'failed': return 'border-red-200 bg-red-50 text-red-700';
      case 'pending': return 'border-amber-200 bg-amber-50 text-amber-700';
      case 'refunded': return 'border-blue-200 bg-blue-50 text-blue-700';
      default: return 'border-slate-200 bg-slate-50 text-slate-700';
    }
  };

  const openPaymentDetails = (payment: PaymentHistoryItem) => {
    setSelectedPayment(payment);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPayment(null);
  };

  const totalPayments = summary?.totalPayments || 0;
  const successfulPayments = summary?.statusCounts.completed || 0;
  const failedPayments = summary?.statusCounts.failed || 0;
  const overallRevenue = summary?.totalAmount || 0;

  const currentMonthRevenue = useMemo(() => {
    if (!paymentHistory.length) return 0;

    const now = new Date();
    return paymentHistory.reduce((sum, payment) => {
      const paymentDate = new Date(payment.payment.paymentDate);
      const isCurrentMonth = !Number.isNaN(paymentDate.getTime())
        && paymentDate.getMonth() === now.getMonth()
        && paymentDate.getFullYear() === now.getFullYear();

      return payment.payment.status === 'completed' && isCurrentMonth
        ? sum + payment.payment.amount
        : sum;
    }, 0);
  }, [paymentHistory]);

  const activeFilterCount = useMemo(() => [
    Boolean(searchTerm.trim()),
    statusFilter !== 'all',
    Boolean(serviceProviderTypeFilter.trim()),
    dateFilter !== 'all',
  ].filter(Boolean).length, [searchTerm, statusFilter, serviceProviderTypeFilter, dateFilter]);

  const paginationItems = useMemo<Array<number | string>>(() => {
    if (!pagination || pagination.totalPages <= 1) return [];

    const items: Array<number | string> = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    if (startPage > 1) {
      items.push(1);
      if (startPage > 2) items.push('ellipsis-start');
    }
    for (let page = startPage; page <= endPage; page += 1) items.push(page);
    if (endPage < pagination.totalPages) {
      if (endPage < pagination.totalPages - 1) items.push('ellipsis-end');
      items.push(pagination.totalPages);
    }
    return items;
  }, [pagination]);

  const handlePageChange = (page: number) => setFilters({ page, limit: 10 });

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setServiceProviderTypeFilter('');
    setDateFilter('all');
    setCustomStartDate(null);
    setCustomEndDate(null);
    clearFilters();
  };

  const resultSummary = loading
    ? 'Loading payments…'
    : pagination && pagination.totalCount > 0
      ? `Showing ${((pagination.currentPage - 1) * pagination.limit) + 1}–${Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} of ${pagination.totalCount} payments`
      : 'No payments to show';

  return (
    <div className="space-y-6">
      <section className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 px-5 py-6 shadow-lg shadow-slate-950/10 sm:px-6">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl" aria-hidden="true" />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-300/20 bg-blue-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-blue-200">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-300" aria-hidden="true" />
              Revenue operations
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">Payments</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
              Track payment performance, transaction status, and service-provider revenue from one workspace.
            </p>
          </div>
          <button
            type="button"
            onClick={refreshPaymentHistory}
            disabled={loading}
            className="inline-flex h-10 items-center justify-center rounded-xl border border-white/15 bg-white/10 px-4 text-sm font-semibold text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5" aria-label="Payment summary">
        <MetricCard label="Monthly revenue" value={`$${currentMonthRevenue.toFixed(2)}`} helper="Current page, this month" icon={CalendarDays} iconClassName="text-violet-700" iconBackgroundClassName="bg-violet-50" />
        <MetricCard label="Overall revenue" value={`$${overallRevenue.toFixed(2)}`} helper="Total recorded earnings" icon={TrendingUp} iconClassName="text-indigo-700" iconBackgroundClassName="bg-indigo-50" />
        <MetricCard label="Total payments" value={totalPayments.toLocaleString()} helper="All transactions" icon={CreditCard} iconClassName="text-blue-700" iconBackgroundClassName="bg-blue-50" />
        <MetricCard label="Successful" value={successfulPayments.toLocaleString()} helper="Completed payments" icon={CheckCircle2} iconClassName="text-emerald-700" iconBackgroundClassName="bg-emerald-50" />
        <MetricCard label="Failed" value={failedPayments.toLocaleString()} helper="Declined payments" icon={XCircle} iconClassName="text-red-700" iconBackgroundClassName="bg-red-50" />
      </section>

      <section className="admin-table-shell" aria-labelledby="payment-history-title">
        <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
              <WalletCards className="h-[18px] w-[18px]" aria-hidden="true" />
            </span>
            <div>
              <h2 id="payment-history-title" className="text-base font-semibold text-slate-950">Payment history</h2>
              <p className="mt-0.5 text-xs text-slate-500" aria-live="polite">{resultSummary}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setAreFiltersOpen((current) => !current)}
            aria-expanded={areFiltersOpen}
            aria-controls="payment-filters"
            className={`inline-flex h-10 items-center justify-center rounded-xl border px-4 text-sm font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${areFiltersOpen || activeFilterCount > 0
              ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
              : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
              }`}
          >
            <SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-blue-700 px-1.5 py-0.5 text-[11px] font-bold leading-none text-white">
                {activeFilterCount}
              </span>
            )}
            <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${areFiltersOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
        </div>

        <div id="payment-filters" hidden={!areFiltersOpen} className="border-b border-slate-200 bg-slate-50/80 px-4 py-5 sm:px-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            <div className="xl:col-span-2">
              <label htmlFor="payment-search" className="mb-1.5 block text-xs font-semibold text-slate-600">Search payments</label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" aria-hidden="true" />
                <input id="payment-search" type="search" placeholder="Customer, transaction ID, service, or reference" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} className={`${inputClassName} pl-10`} />
              </div>
            </div>
            <div>
              <label htmlFor="payment-status-filter" className="mb-1.5 block text-xs font-semibold text-slate-600">Status</label>
              <select id="payment-status-filter" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)} className={inputClassName}>
                <option value="all">All statuses</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div>
              <label htmlFor="provider-type-filter" className="mb-1.5 block text-xs font-semibold text-slate-600">Provider type</label>
              <input id="provider-type-filter" type="text" placeholder="e.g. Electrician" value={serviceProviderTypeFilter} onChange={(event) => setServiceProviderTypeFilter(event.target.value)} className={inputClassName} />
            </div>
            <div>
              <label htmlFor="payment-date-filter" className="mb-1.5 block text-xs font-semibold text-slate-600">Date</label>
              <select id="payment-date-filter" value={dateFilter} onChange={(event) => setDateFilter(event.target.value)} className={inputClassName}>
                <option value="all">All dates</option>
                <option value="today">Today</option>
                <option value="week">Last 7 days</option>
                <option value="month">Last 30 days</option>
                <option value="custom">Custom range</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            {dateFilter === 'custom' ? (
              <div className="grid flex-1 grid-cols-1 gap-4 sm:max-w-md sm:grid-cols-2">
                <div>
                  <label htmlFor="payment-start-date" className="mb-1.5 block text-xs font-semibold text-slate-600">Start date</label>
                  <input id="payment-start-date" type="date" value={customStartDate || ''} onChange={(event) => setCustomStartDate(event.target.value)} className={inputClassName} />
                </div>
                <div>
                  <label htmlFor="payment-end-date" className="mb-1.5 block text-xs font-semibold text-slate-600">End date</label>
                  <input id="payment-end-date" type="date" value={customEndDate || ''} onChange={(event) => setCustomEndDate(event.target.value)} className={inputClassName} />
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-500">Filters update the payment list automatically.</p>
            )}
            {activeFilterCount > 0 && (
              <button type="button" onClick={handleClearFilters} className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2">
                <X className="mr-2 h-4 w-4" aria-hidden="true" />
                Clear filters
              </button>
            )}
          </div>
        </div>

        <div className="admin-table-scroll" role="region" aria-label="Payment history data table" tabIndex={0}>
          <table className="admin-data-table min-w-max">
            <thead><tr><th>Status</th><th>Transaction ID</th><th>Service Provider</th><th>Contact</th><th>Date</th><th>Amount</th><th>Method</th><th>Membership</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="py-14 text-center"><div className="flex flex-col items-center"><LoaderCircle className="mb-3 h-8 w-8 animate-spin text-blue-600" aria-hidden="true" /><h3 className="text-sm font-semibold text-slate-900">Loading payments…</h3><p className="mt-1 text-sm text-slate-500">Fetching the latest transaction history.</p></div></td></tr>
              ) : error ? (
                <tr><td colSpan={8} className="py-14 text-center"><div className="mx-auto flex max-w-md flex-col items-center"><span className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-red-600"><CircleAlert className="h-5 w-5" aria-hidden="true" /></span><h3 className="text-sm font-semibold text-slate-900">Unable to load payments</h3><p className="mt-1 text-sm text-slate-500">{error}</p><button type="button" onClick={refreshPaymentHistory} className="mt-4 inline-flex h-9 items-center rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white transition-colors hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2">Try again</button></div></td></tr>
              ) : paymentHistory.length === 0 ? (
                <tr><td colSpan={8} className="py-14 text-center"><div className="mx-auto flex max-w-md flex-col items-center"><span className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-500"><CreditCard className="h-5 w-5" aria-hidden="true" /></span><h3 className="text-sm font-semibold text-slate-900">No payments found</h3><p className="mt-1 text-sm text-slate-500">Try adjusting your payment filters.</p></div></td></tr>
              ) : paymentHistory.map((payment) => (
                <tr key={payment.id}>
                  <td className="whitespace-nowrap"><span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusColor(payment.payment.status)}`}>{formatStatus(payment.payment.status)}</span></td>
                  <td className="whitespace-nowrap"><button type="button" onClick={() => openPaymentDetails(payment)} className="inline-flex items-center font-semibold text-blue-700 hover:text-blue-900" aria-label={`View payment ${payment.payment.transactionId}`}>{payment.payment.transactionId}<ArrowUpRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" /></button></td>
                  <td className="whitespace-nowrap"><div className="font-medium text-slate-900">{payment.serviceProvider.fullName}</div><div className="text-xs text-slate-500">{payment.serviceProvider.serviceProviderType}</div></td>
                  <td className="whitespace-nowrap"><div className="text-slate-900">{payment.serviceProvider.contactNumber}</div><div className="text-xs text-slate-500">{payment.serviceProvider.email}</div></td>
                  <td className="whitespace-nowrap text-slate-700">{formatDateString(payment.payment.paymentDate)}</td>
                  <td className="whitespace-nowrap font-semibold text-slate-950">${(payment.payment.amount || 0).toFixed(2)}</td>
                  <td className="whitespace-nowrap text-slate-700">{payment.payment.paymentMethod}</td>
                  <td className="whitespace-nowrap text-slate-700">{payment.payment.membershipType}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {pagination && pagination.totalPages > 1 && (
        <div className="admin-table-pagination">
          <div className="flex flex-1 justify-between sm:hidden">
            <button type="button" onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={!pagination.hasPrevPage} className="inline-flex h-10 items-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">Previous</button>
            <button type="button" onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={!pagination.hasNextPage} className="inline-flex h-10 items-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">Next</button>
          </div>
          <div className="hidden flex-1 items-center justify-between sm:flex">
            <p className="text-sm text-slate-600">Page <span className="font-semibold text-slate-900">{pagination.currentPage}</span> of <span className="font-semibold text-slate-900">{pagination.totalPages}</span></p>
            <nav className="inline-flex" aria-label="Pagination">
              <button type="button" onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={!pagination.hasPrevPage} className="inline-flex items-center justify-center bg-white text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"><span className="sr-only">Previous page</span><ChevronLeft className="h-4 w-4" aria-hidden="true" /></button>
              {paginationItems.map((item) => typeof item === 'number' ? (
                <button type="button" key={item} onClick={() => handlePageChange(item)} aria-current={item === pagination.currentPage ? 'page' : undefined} className={item === pagination.currentPage ? 'bg-slate-900 font-semibold text-white' : 'bg-white font-medium text-slate-700 hover:bg-slate-50'}>{item}</button>
              ) : (
                <span key={item} className="inline-flex items-center justify-center bg-white text-sm text-slate-400">…</span>
              ))}
              <button type="button" onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={!pagination.hasNextPage} className="inline-flex items-center justify-center bg-white text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"><span className="sr-only">Next page</span><ChevronRight className="h-4 w-4" aria-hidden="true" /></button>
            </nav>
          </div>
        </div>
      )}

      {isModalOpen && selectedPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="payment-details-title" onMouseDown={(event) => { if (event.target === event.currentTarget) closeModal(); }}>
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><CreditCard className="h-5 w-5" aria-hidden="true" /></span><div><h2 id="payment-details-title" className="text-lg font-semibold text-slate-950">Payment details</h2><p className="text-xs text-slate-500">Transaction and service-provider record</p></div></div>
              <button type="button" onClick={closeModal} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2" aria-label="Close payment details"><X className="h-4 w-4" aria-hidden="true" /></button>
            </div>
            <dl className="grid max-h-[65vh] grid-cols-1 gap-x-6 gap-y-5 overflow-y-auto px-5 py-5 sm:grid-cols-2">
              <DetailItem label="Transaction ID" wide>{selectedPayment.payment.transactionId}</DetailItem>
              <DetailItem label="Status"><span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusColor(selectedPayment.payment.status)}`}>{formatStatus(selectedPayment.payment.status)}</span></DetailItem>
              <DetailItem label="Amount">${(selectedPayment.payment.amount || 0).toFixed(2)}</DetailItem>
              <DetailItem label="Payment date">{formatDateString(selectedPayment.payment.paymentDate)}</DetailItem>
              <DetailItem label="Payment method">{selectedPayment.payment.paymentMethod}</DetailItem>
              <DetailItem label="Membership">{selectedPayment.payment.membershipType}</DetailItem>
              <DetailItem label="Service provider">{selectedPayment.serviceProvider.fullName}</DetailItem>
              <DetailItem label="Provider type">{selectedPayment.serviceProvider.serviceProviderType}</DetailItem>
              <DetailItem label="Contact number">{selectedPayment.serviceProvider.contactNumber}</DetailItem>
              <DetailItem label="Email">{selectedPayment.serviceProvider.email}</DetailItem>
              <DetailItem label="Registered on" wide>{formatDateTimeString(selectedPayment.serviceProvider.registeredOn)}</DetailItem>
            </dl>
            <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-4"><button type="button" onClick={closeModal} className="inline-flex h-10 items-center justify-center rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white transition-colors hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2">Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
};
